import { AppError } from "@/domain/errors/AppError";
import { getErrorMessage } from "@/domain/errors/getErrorMessage";

/**
 * Extract a user-safe error message from any error.
 * If it's an ApiClientError, returns the clean backend message (no endpoint prefix).
 * Otherwise delegates to the domain-level getErrorMessage().
 */
export function getUserErrorMessage(error: unknown, fallback = "Error desconocido"): string {
  if (error instanceof ApiClientError) return error.userMessage;
  return getErrorMessage(error, fallback);
}

export interface ApiClientErrorData {
  message: string;
  status: number;
  code?: string;
  details?: Record<string, unknown>;
  endpoint?: string;
  method?: string;
}

export class ApiClientError extends AppError {
  readonly isOperational = true;
  readonly code: string;
  public readonly status: number;
  public readonly details?: Record<string, unknown>;
  public readonly endpoint?: string;
  public readonly method?: string;
  /** The original backend error message without endpoint prefix. Safe to show to users. */
  public readonly userMessage: string;

  constructor(error: ApiClientErrorData) {
    // Prefixed message for logging/debugging (shown in console, Sentry, etc.)
    const debugMessage = error.endpoint
      ? `${error.method || "GET"} ${error.endpoint}: ${error.message}`
      : error.message;
    super(debugMessage);
    this.name = "ApiClientError";
    this.status = error.status;
    this.code = error.code ?? "UNKNOWN";
    this.details = error.details;
    this.endpoint = error.endpoint;
    this.method = error.method;
    // Original backend message — no endpoint prefix, safe for UI
    this.userMessage = error.message;
  }
}
